import React, { useContext, useEffect, useMemo } from 'react';

// DEPENDENCIES
import { Link, useParams } from 'react-router-dom';
import find from 'lodash/find';
import isUndefined from 'lodash/isUndefined';
import isNil from 'lodash/isNil';
import { useFormikContext } from 'formik';

// GLOBAL FUNCTIONS
import { makePath } from 'functions.js';

// GLOBAL VARIABLES
import { ENDPOINTS } from 'endpoints.js';
import { INTERFACE, RECORD } from 'pathnames.js';
import { NEW_USED, RETAIL_WHOLESALE, WHOLESALE_FORMAT as WHOLESALE_SIMPLIFIED } from 'codes';
import { INVENTORY_FILE_TYPE_IDS } from 'globals.js';

// GLOBAL HELPERS/CONTEXT
import { RecordContext } from 'helpers/getRecordData';
import useRecordView from 'helpers/getRecordView';

// GLOBAL COMPONENTS
import Form from 'core/form/Form';
import Card from 'parts/card/Card';

// LOCAL VARIABLES
import { SALE_TYPES_NEW, SALE_TYPES_PRE, WHOLESALE_FORMAT } from '../variables';
import { WARRANTY_TYPES_OPTION_KEYS } from '../../../sections/variables.js';

// LOCAL COMPONENTS
import DateSold from '../tools/DateSold';
import Attachment from 'routers/interface/tools/attachment/Attachment';
import SaleSubtype from '../tools/SaleSubtype';
import SaveField from '../../../tools/SaveField';

// MAIN COMPONENT
const VehicleInfoForm = ({ updateSaleType }) => {
  const { recordID } = useParams();

  // CONTEXT
  const { record: { inventory = {}, ...record } = {} } = useContext(RecordContext);
  const { values, setFieldValue } = useFormikContext() || {};
  const { isAdd } = useRecordView();

  // MEMOS
  const {
    current_mileage,
    daily_sales_inventory_id,
    is_cpo,
    is_loaner,
    sale_type_category_id,
    wholesale_format_id,
    warranty_type
  } = useMemo(() => values, [values]);
  const { is_for_wholesale, mileage, new_used, stock_num, ...vehicleInfo } = useMemo(
    () => (isAdd ? record : inventory),
    [record, inventory, isAdd]
  );
  const type = useMemo(() => find(NEW_USED, o => o.numeric === parseInt(new_used))?.label || '', [new_used]);
  const isNew = useMemo(() => parseInt(new_used) === NEW_USED.new.numeric, [new_used]);
  const isWholesale = useMemo(
    () => parseInt(sale_type_category_id) === RETAIL_WHOLESALE.wholesale.numeric,
    [sale_type_category_id]
  );
  const isSimplified = useMemo(
    () => parseInt(wholesale_format_id) === WHOLESALE_SIMPLIFIED.simplified.numeric,
    [wholesale_format_id]
  );

  const salesTypeOptions = useMemo(() => (isNew ? SALE_TYPES_NEW : SALE_TYPES_PRE), [isNew]);
  const mileageClass = useMemo(
    () => (parseInt(current_mileage) < parseInt(mileage) ? 'text-danger' : undefined),
    [current_mileage, mileage]
  );
  const mileageText = useMemo(
    () =>
      current_mileage && parseInt(current_mileage) <= 0
        ? 'Please enter a value greater than or equal to 1'
        : parseInt(current_mileage) < parseInt(mileage)
        ? 'Warning: The Current Mileage value you have entered is lower than the Mileage.'
        : '',
    [current_mileage, mileage]
  );

  // EFFECTS
  useEffect(() => {
    if (!isNew) {
      if (isUndefined(is_cpo) && isUndefined(is_loaner)) {
        setFieldValue(`sale_subtype_name`, 'Pending (Answer Above for Results)');
      } else if (String(is_cpo) === 'true' && String(is_loaner) === 'true') {
        setFieldValue(`sale_subtype_name`, 'CPO Loaner (PL-CPO)');
        setFieldValue(`sale_subtype_id`, 4);
      } else if (String(is_cpo) === 'false' && String(is_loaner) === 'true') {
        setFieldValue(`sale_subtype_name`, 'Pre-Owned Loaner (PL)');
        setFieldValue(`sale_subtype_id`, 3);
      } else if (String(is_cpo) === 'true' && String(is_loaner) === 'false') {
        setFieldValue(`sale_subtype_name`, 'Certified Pre-Owned Standard (PS-CPO)');
        setFieldValue(`sale_subtype_id`, 2);
      } else if (String(is_cpo) === 'false' && String(is_loaner) === 'false') {
        setFieldValue(`sale_subtype_name`, 'Pre-Owned Standard (PS)');
        setFieldValue(`sale_subtype_id`, 1);
      }
    }
  }, [is_cpo, is_loaner, isNew, setFieldValue]);

  useEffect(() => {
    if (+sale_type_category_id === RETAIL_WHOLESALE.wholesale.numeric && isNil(wholesale_format_id)) {
      setFieldValue('wholesale_format_id', 1);
    }
    if (isAdd) {
      if (+sale_type_category_id === RETAIL_WHOLESALE.wholesale.numeric) setFieldValue('customer_type_id', 2);
      else setFieldValue('customer_type_id', 1);
    }
  }, [sale_type_category_id, isAdd, wholesale_format_id, setFieldValue]);

  return (
    <Form.Body>
      <Form.Checklist
        type="radio"
        name="sale_type_category_id"
        label="Sale Type"
        options={salesTypeOptions}
        onValueChange={updateSaleType}
        plaintext={!isAdd && isWholesale && isSimplified}
        required
      />
      <Form.Checklist
        type="radio"
        name="wholesale_format_id"
        label="Format"
        options={WHOLESALE_FORMAT}
        onValueChange={updateSaleType}
        plaintext={!isAdd}
        hidden={!isWholesale}
        required={isWholesale}
      />
      <DateSold />
      <Form.PlainText
        label="Stock #"
        value={<Link to={makePath(INTERFACE.inventory, RECORD.view, daily_sales_inventory_id)}>{stock_num}</Link>}
      />
      <Form.PlainText label="VIN" value={vehicleInfo.vin_num} />
      {!!vehicleInfo.vin_num &&
        <Attachment.Upload
          label="Photo Proof of VIN"
          name="inventoryDocumentAttachment.vin_photo"
          uploadType="inventory"
          documentType={isNew ? INVENTORY_FILE_TYPE_IDS.VIN_PHOTO_NEW : INVENTORY_FILE_TYPE_IDS.VIN_PHOTO_USED}
          documentCode={isNew ? 'vin_photo_new' : 'vin_photo_used'}
          requestedFor="Inventory"
        />
      }
      <Form.PlainText label="Type" value={type} />
      <SaleSubtype />
      <Form.PlainText label="Year" value={vehicleInfo.vehicle_year} />
      <Form.PlainText label="Make" value={vehicleInfo.make} />
      <Form.PlainText label="Model" value={vehicleInfo.model} />
      <Form.PlainText label="Trim" value={vehicleInfo.vehicle_trim} />
      <Form.PlainText label="Body Style" value={vehicleInfo.body_style} />
      <Form.PlainText label="Engine" value={vehicleInfo.engine} />
      <Form.PlainText label="Engine Type" value={vehicleInfo.engine_type} />
      <Form.PlainText label="Transmission" value={vehicleInfo.transmission_name} />
      <Form.PlainText label="Drivetrain" value={vehicleInfo.drivetrain_name} />
      <Form.PlainText label="Exterior" value={vehicleInfo.ext_color} />
      <Form.PlainText label="Interior" value={vehicleInfo.int_color} />
      <Form.PlainText label="Age" value={vehicleInfo.age_of_vehicle} />
      {isAdd ? (
        <Form.PlainText value={vehicleInfo.warranty_type_name} label="Warranty Type" />
      ) : (
        <SaveField
          useButton
          endpoint={ENDPOINTS.sales.updateWarrantyType(recordID)}
          params={{
            warrantyType: warranty_type
          }}
        >
          <Form.Select
            name="warranty_type"
            optionKeys={WARRANTY_TYPES_OPTION_KEYS}
            label="Warranty Type"
            lookup="warrantyTypes"
          />
        </SaveField>
      )}

      <Form.PlainText label="Mileage" value={mileage} schema="miles" />
      <Form.Miles
        label="Current Mileage"
        name="current_mileage"
        input={{ className: mileageClass }}
        min={1}
        required
        error={mileageText}
      />
      <Attachment.Upload
        label="Photo Proof of Mileage"
        name="inventoryDocumentAttachment.mileage_photo"
        uploadType="inventory"
        documentType={isNew ? INVENTORY_FILE_TYPE_IDS.MILEAGE_PHOTO_NEW : INVENTORY_FILE_TYPE_IDS.MILEAGE_PHOTO_USED}
        documentCode={isNew ? 'mileage_photo_new' : 'mileage_photo_used'}
        requestedFor="Inventory"
      />
    </Form.Body>
  );
};

// EXPORT
export default function VehicleInfo(props) {
  return <Card title="Vehicle Info" body={<VehicleInfoForm {...props} />} />;
}
